// Finder — the pure read-model behind the fuzzy content-finder.
//
// The finder is "presentation as a projection over the one grounded model"
// (docs/model/principles.md — "One grounded model, many projections"): the
// index owns NO private state; it is a pure fold over a `FinderDoc[]` snapshot
// the shell projects from resource state (source content + material element
// text) and hands in. Titles are the cheap half; the finder's real corpus is
// the bodies (.claude/analysis/fuzzy-finder.md §3/§4b).
//
// Placement note (a flagged fork): the design doc's first choice was an
// APP-LAYER `buildFinderIndex(getState)` workflow. The mock/http port seam the
// UI actually talks to is per-resource and fixture-backed — there is no
// `getState()` in the shell — so the SIMPLEST wiring that keeps `@bilda/core`
// pure is: a thin content port (`AppPorts.loadFinderIndex`) supplies the
// `FinderDoc[]` snapshot, and THIS pure module folds it into ranked hits. Core
// stays untouched; the projection stays pure; only the impure port reaches out.
//
// Discipline (dev-ts): every varying shape is a named discriminated union with
// a `type` discriminant; no meaning-swapping boolean; no `as`. Scored through
// the ONE house `scoreFuzzy` (Search/query.ts), shared with the omnibar.

import {
    type FuzzyMatch,
    type MatchRange,
    scoreFuzzy,
} from "../Search/query.js";

// The resource kinds the finder searches. Mirrors the live SearchKind minus
// `project` (the finder is about content, and projects have no body). "export"
// is the current name for the deliverable resource (Package→Export rename).
export type FinderKind = "source" | "material" | "export";

// One addressable line of a resource's body, as the port projects it. The
// `elementPath` is the domain ElementPath (root-to-node id chain) so "jump to
// match" can address the element by its id chain; `heading` is the
// nearest section title, shown as breadcrumb context in the preview.
export type FinderLine = {
    readonly elementPath: readonly string[];
    readonly heading: string;
    readonly text: string;
};

// One resource projected for the finder: a title plus an ordered body. Sources
// carry extracted chunk/block text; materials carry element-tree text; both
// flatten to lines. This is the snapshot the impure port hands the pure fold.
export type FinderDoc = {
    readonly resourceId: string;
    readonly kind: FinderKind;
    readonly title: string;
    readonly lines: readonly FinderLine[];
};

// WHERE a hit was found — a named sum, never a boolean "isTitle". A title hit
// carries nothing extra; a content hit carries the element address the preview
// centres on and "jump to element" navigates to.
export type FinderField =
    | { readonly type: "title" }
    | {
          readonly type: "content";
          readonly elementPath: readonly string[];
          readonly heading: string;
          readonly lineIndex: number;
      };

// One searchable unit + the resource it belongs to. `text` is the exact string
// scored; `field` says which part of the resource it is.
export type FinderCandidate = {
    readonly resourceId: string;
    readonly kind: FinderKind;
    readonly resourceTitle: string;
    readonly field: FinderField;
    readonly text: string;
};

// A scored candidate. `match.ranges` index into `candidate.text`.
export type FinderHit = {
    readonly candidate: FinderCandidate;
    readonly match: FuzzyMatch;
};

// Where selecting a hit navigates: a resource, optionally a specific element
// inside it. The shell resolves `resourceId` to a ResourceRef and opens it;
// `elementPath` is present for a content hit so a later slice can scroll to the
// exact element. Its own named type — the finder's one
// outward intent.
export type FinderTarget = {
    readonly resourceId: string;
    readonly kind: FinderKind;
    readonly elementPath?: readonly string[];
};

// The search scope — the Sublime-style mode sigil (docs §3/§7). A named sum, one
// variant per intent: `all` searches titles + bodies; `content` (the `#` sigil)
// searches bodies only. Not a boolean flag on one candidate list — the mode
// changes WHICH candidates exist.
export type FinderMode =
    | { readonly type: "all" }
    | { readonly type: "content" };

// Interpret a raw prompt into a mode + the needle to score. A leading `#`
// (Sublime's content sigil, docs §3) switches to content-only and is stripped
// from the needle; everything else is an `all`-mode text query. Total — a lone
// `#` mid-typing is a valid "content mode, empty needle" query, not an error.
export type InterpretedQuery = {
    readonly mode: FinderMode;
    readonly needle: string;
};

export const interpretQuery = (raw: string): InterpretedQuery =>
    raw.startsWith("#")
        ? { mode: { type: "content" }, needle: raw.slice(1).trimStart() }
        : { mode: { type: "all" }, needle: raw };

// Flatten docs into candidates for a mode. `all` emits a title candidate plus
// one per line; `content` skips titles. Pure.
export const buildCandidates = (
    docs: readonly FinderDoc[],
    mode: FinderMode,
): readonly FinderCandidate[] =>
    docs.flatMap((doc) => {
        const titleCandidate: readonly FinderCandidate[] =
            mode.type === "all"
                ? [
                      {
                          resourceId: doc.resourceId,
                          kind: doc.kind,
                          resourceTitle: doc.title,
                          field: { type: "title" },
                          text: doc.title,
                      },
                  ]
                : [];
        const lineCandidates = doc.lines.map(
            (line, lineIndex): FinderCandidate => ({
                resourceId: doc.resourceId,
                kind: doc.kind,
                resourceTitle: doc.title,
                field: {
                    type: "content",
                    elementPath: line.elementPath,
                    heading: line.heading,
                    lineIndex,
                },
                text: line.text,
            }),
        );
        return [...titleCandidate, ...lineCandidates];
    });

// Score every candidate, drop the non-matches, sort best-first. Pure. An empty
// query returns every candidate (score 0) in input order — the resting list.
export const rankHits = (
    query: string,
    candidates: readonly FinderCandidate[],
): readonly FinderHit[] => {
    const hits: FinderHit[] = [];
    for (const candidate of candidates) {
        const match = scoreFuzzy(query, candidate.text);
        if (match !== null) hits.push({ candidate, match });
    }
    return [...hits].sort((a, b) => b.match.score - a.match.score);
};

// The target selecting a hit navigates to — pure derivation off the candidate.
export const targetOf = (candidate: FinderCandidate): FinderTarget =>
    candidate.field.type === "content"
        ? {
              resourceId: candidate.resourceId,
              kind: candidate.kind,
              elementPath: candidate.field.elementPath,
          }
        : { resourceId: candidate.resourceId, kind: candidate.kind };

// A rendered run of text: matched (mark it) or not. Pure — splits `text` at the
// half-open `ranges` so the view maps segments to <mark>/<span> without any
// index arithmetic of its own.
export type TextSegment = { readonly text: string; readonly matched: boolean };

export const segmentByRanges = (
    text: string,
    ranges: readonly MatchRange[],
): readonly TextSegment[] => {
    if (ranges.length === 0) return [{ text, matched: false }];
    const segments: TextSegment[] = [];
    let cursor = 0;
    for (const range of ranges) {
        if (range.start > cursor)
            segments.push({
                text: text.slice(cursor, range.start),
                matched: false,
            });
        segments.push({
            text: text.slice(range.start, range.end),
            matched: true,
        });
        cursor = range.end;
    }
    if (cursor < text.length)
        segments.push({ text: text.slice(cursor), matched: false });
    return segments;
};
